import { useEffect, useRef, useCallback, useSyncExternalStore } from 'react';
import { supabase } from '../supabase';

/**
 * Centralized auth token management following Supabase's recommended pattern.
 *
 * From Supabase docs:
 * "It's best practice and highly recommended to extract the access token (JWT)
 * and store it in memory for further use in your application. Avoid frequent
 * calls to supabase.auth.getSession() for the same purpose. There is a background
 * process that keeps track of when the session should be refreshed so you will
 * always receive valid tokens by listening to TOKEN_REFRESHED event."
 */

type TokenListener = () => void;

class AuthTokenStore {
  private token: string | null = null;
  private listeners = new Set<TokenListener>();
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.initPromise = this.initialize();
  }

  private async initialize() {
    if (this.initialized) return;

    const {
      data: { session },
    } = await supabase.auth.getSession();
    this.token = session?.access_token ?? null;
    this.initialized = true;

    supabase.auth.onAuthStateChange((event, session) => {
      const newToken = session?.access_token ?? null;

      if (this.token !== newToken) {
        this.token = newToken;
        this.notifyListeners();
      }
    });
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }

  subscribe(listener: TokenListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): string | null {
    return this.token;
  }

  getServerSnapshot(): string | null {
    return null;
  }

  async waitForInit(): Promise<void> {
    await this.initPromise;
  }

  async getToken(): Promise<string | null> {
    await this.initPromise;
    return this.token;
  }
}

export const authTokenStore = new AuthTokenStore();

/**
 * Hook to get the current auth token reactively.
 * Updates automatically when token changes (sign in, sign out, refresh).
 */
export function useAuthToken(): string | null {
  return useSyncExternalStore(
    authTokenStore.subscribe.bind(authTokenStore),
    authTokenStore.getSnapshot.bind(authTokenStore),
    authTokenStore.getServerSnapshot.bind(authTokenStore)
  );
}

/**
 * Hook that provides a stable reference to the current token for use in callbacks.
 * This avoids stale closure issues when using the token in async operations.
 */
export function useAuthTokenRef(): React.RefObject<string | null> {
  const tokenRef = useRef<string | null>(null);
  const token = useAuthToken();

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  return tokenRef;
}

/**
 * Get auth headers for API calls.
 * This is the recommended way to get headers as it uses the in-memory token
 * which is kept fresh by the auth state change listener.
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await authTokenStore.getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Hook that returns a memoized function to get auth headers.
 * Useful for components that need to make authenticated requests.
 */
export function useGetAuthHeaders() {
  const tokenRef = useAuthTokenRef();

  return useCallback((): HeadersInit => {
    const token = tokenRef.current;
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [tokenRef]);
}
